import Vue from 'vue';
import Router, { RouterOptions } from 'vue-router';
import { isPartner, isClient, SessionStorage, isOfficial } from '@witkac/kartamiejska-shared';

import { DEFAULT_LIST_COUNT } from '@/utils/constants';
import PageNotFound from '@/views/PageNotFound.vue';
import Confirm from '@/views/confirm/Confirm.vue';
import Login from '@/views/login/Login.vue';
import AppLayout from '@/AppLayout.vue';
import Dashboard from '@/views/dashboard/Dashboard.vue';
import Partners from '@/views/partners/List.vue';
import PartnerView from '@/views/partners/PartnerView.vue';
import PartnerProfile from '@/views/partners/Detail.vue';
import Benefits from '@/views/partners/tabs/Benefits.vue';
import benefitsHistory from '@/components/BenefitUsageHistory.vue';
import EntityHistory from '@/components/EntityHistory.vue';
import Places from '@/views/partners/tabs/Places.vue';
import Groups from '@/views/groups/List.vue';
import ClientProfileSearch from '@/views/client/Search.vue';
import ClientProfile from '@/views/client/Profile.vue';
import Profile from '@/views/profile/Profile.vue';
import OfficialsList from '@/views/officials/List.vue';
import OfficialProfile from '@/views/officials/Profile.vue';
import PartnerEmployees from '@/views/employees/List.vue';
import Reset from '@/views/reset/Reset.vue';
import ResetPassword from '@/views/reset/ResetPassword.vue';
import Settings from '@/views/settings/List.vue';
import TemplatesList from '@/views/mailTemplates/List.vue';
import EmployeeProfile from '@/views/employees/Profile.vue';
import PlaceSelect from '@/views/partners/PlaceSelect.vue';
import RolesTree from '@/views/roles/List.vue';
import RolePermission from '@/views/roles/permissions/List.vue';
import Categories from '@/views/categories/List.vue';
import Terms from '@/views/terms/List.vue';
import Pages from '@/views/pages/List.vue';
import userRoles from '@/views/roles/roleAddUser.vue';
import termsCurrent from '@/views/terms/CurrentList.vue';
import store from '@/store/store';
import ClientView from '@/views/client/ClientView.vue';
import clientCards from '@/views/client/tabs/Cards.vue';
import clientGroups from '@/views/client/tabs/Groups.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login
    },
    {
      path: '/confirm/:token',
      name: 'confirm',
      component: Confirm
    },
    {
      path: '/reset',
      name: 'reset',
      component: Reset
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: ResetPassword
    },
    {
      path: '/place',
      name: 'placeSelect',
      component: PlaceSelect,
      meta: {
        toolbarTitle: 'Wybór obiektu',
        authorized: true
      }
    },
    {
      path: '/termsCurrent',
      name: 'termsCurrent',
      component: termsCurrent,
      beforeEnter: (to, from, next) => {
        Promise.all([
          store.dispatch('consents/list', { count: 0, userId: sessionStorage.getItem(SessionStorage.USER_ID) }),
          store.dispatch('terms/termsCurrent', {
            profileType: sessionStorage.getItem(SessionStorage.PROFILE_TYPE),
            required: true
          })
        ])
          .then(() => next())
          .catch(() => () => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy regulaminów' }));
      },
      meta: {
        toolbarTitle: 'Lista regulaminów do zaakceptowania'
      }
    },
    {
      path: '/',
      component: AppLayout,
      meta: {
        authorized: true
      },
      children: [
        {
          path: '',
          redirect: { name: 'dashboard' }
        },

        {
          path: 'dashboard',
          name: 'dashboard',
          component: Dashboard,
          beforeEnter: (to, from, next) => {
            if (isPartner()) {
              if (!sessionStorage.getItem(SessionStorage.PLACE_ID)) {
                next('/place');
              }
              store.commit('breadcrumbs/set', [{ text: 'Podsumowanie', disabled: true }]);

              const partnerId = parseInt(sessionStorage.getItem(SessionStorage.PARTNER_ID) as string);

              Promise.all([
                store.dispatch('stats/getCardUsages', partnerId),
                store.dispatch('stats/getBenefitsValue', partnerId)
              ])
                .then(() => next())
                .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
            } else {
              store.commit('breadcrumbs/set', [{ text: 'Panel administratora', disabled: true }]);

              Promise.all([
                store.dispatch('partners/list'),
                store.dispatch('stats/getCardUsages', undefined),
                store.dispatch('stats/getBenefitsValue', undefined),
                store.dispatch('stats/getPartnersCreated'),
                store.dispatch('stats/getActivatedCards'),
                store.dispatch('stats/getUnconfirmedProfiles'),
                store.dispatch('stats/getReadyCards')
              ])
                .then(() => next())
                .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać statystyk' }));
            }
          },
          meta: {
            toolbarTitle: 'Podsumowanie'
          }
        },
        {
          path: 'profile',
          name: 'profile',
          component: Profile,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Profil', disabled: true }]);

            const storeName = isClient() ? 'client' : 'officials';
            store.commit(`${storeName}/setId`, sessionStorage.getItem(SessionStorage.USER_ID));
            store
              .dispatch(`${storeName}/get`)
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych profilu' }));
          },
          meta: {
            toolbarTitle: 'Twój profil'
          }
        },
        {
          path: 'partners',
          name: 'partners',
          component: Partners,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Partnerzy', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Partnerzy karty miejskiej'
          }
        },
        {
          path: 'partners/:id(\\d+)',
          component: PartnerView,
          beforeEnter: (to, from, next) => {
            const partnerId = parseInt(to.params.id);

            store.commit('partners/setId', partnerId);

            store
              .dispatch('partners/get')
              .then(res => {
                const crumbs: any[] = [{ text: res.name, disabled: true }];
                if (isOfficial()) {
                  crumbs.unshift({ text: 'Partnerzy', to: { name: 'partners' }, exact: true });
                }
                store.commit('breadcrumbs/set', crumbs);
              })
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
          },
          children: [
            {
              path: '',
              name: 'partner',
              component: PartnerProfile,
              meta: {
                toolbarTitle: 'Profil partnera'
              }
            },
            {
              path: 'benefits',
              name: 'partnerBenefits',
              component: Benefits,
              beforeEnter: (to, from, next) => {
                const partnerId = parseInt(to.params.id);

                Promise.all([
                  store.dispatch('groups/list'),
                  store.dispatch('categories/list'),
                  store.dispatch('partners/places/list', { count: 0, partnerId }),
                  store.dispatch('partners/benefits/list', { partnerId, count: 0 })
                ])
                  .then(() => next())
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
              },
              meta: {
                toolbarTitle: 'Profil partnera'
              }
            },
            {
              path: 'usage',
              name: 'partnerUsage',
              component: benefitsHistory,
              props: { getPartner: true },
              beforeEnter: (to, from, next) => {
                const partnerId = parseInt(to.params.id);

                store
                  .dispatch('benefitsHistory/list', {
                    start: 0,
                    count: DEFAULT_LIST_COUNT,
                    partnerId,
                    desc: true
                  })
                  .then(() => next())
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
              },
              meta: {
                toolbarTitle: 'Profil partnera'
              }
            },
            {
              path: 'history',
              name: 'partnerHistory',
              component: EntityHistory,
              props: { getPartner: true },
              beforeEnter: (to, from, next) => {
                store
                  .dispatch('entityHistory/list', {
                    tableName: 'Partner',
                    start: 0,
                    count: DEFAULT_LIST_COUNT,
                    entityId: to.params.id,
                    desc: true
                  })
                  .then(() => next())
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
              },
              meta: {
                toolbarTitle: 'Profil partnera'
              }
            },
            {
              path: 'places',
              name: 'partnerPlaces',
              component: Places,
              meta: {
                toolbarTitle: 'Profil partnera'
              }
            }
          ]
        },
        {
          path: 'partners/:id(\\d+)/employees',
          name: 'employeesById',
          component: PartnerEmployees,
          beforeEnter: (to, from, next) => {
            const partnerId = parseInt(to.params.id);

            store.commit('partners/setId', partnerId);

            store
              .dispatch('partners/get')
              .then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
                  { text: res.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
                  { text: 'Pracownicy', disabled: true }
                ]);

                next();
              })
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
          },
          meta: {
            toolbarTitle: 'Pracownicy'
          }
        },
        {
          path: 'partners/:id(\\d+)/employees/:employeeId',
          name: 'employeeProfile',
          component: EmployeeProfile,
          beforeEnter: (to, from, next) => {
            const partnerId = parseInt(to.params.id);

            store.commit('partners/setId', partnerId);
            store.commit('employees/setId', to.params.employeeId);

            Promise.all([store.dispatch('employees/get'), store.dispatch('partners/get')])
              .then(([res, partnerRes]) => {
                store.commit('breadcrumbs/set', [
                  { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
                  { text: partnerRes.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
                  { text: 'Pracownicy', to: { name: 'employeesById', params: { id: partnerId } }, exact: true },
                  { text: res.firstName + ' ' + res.lastName, disabled: true }
                ]);
                next();
              })
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
          },
          meta: {
            toolbarTitle: 'Pracownik'
          }
        },
        {
          path: 'partners/:id(\\d+)/employees/roles/:employeeId',
          name: 'employeeOfficialRoles',
          component: userRoles,
          beforeEnter: (to, from, next) => {
            const partnerId = parseInt(to.params.id);

            store.commit('partners/setId', partnerId);
            store.commit('employees/setId', to.params.employeeId);

            Promise.all([
              store.dispatch(`profiles/userRole/list`, to.params.employeeId),
              store.dispatch(`roles/list`, { count: 0 }),
              Promise.all([store.dispatch('employees/get'), store.dispatch('partners/get')]).then(
                ([res, partnerRes]) => {
                  store.commit('breadcrumbs/set', [
                    { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
                    { text: partnerRes.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
                    { text: 'Pracownicy', to: { name: 'employeesById', params: { id: partnerId } }, exact: true },
                    { text: res.firstName + ' ' + res.lastName, disabled: true },
                    { text: 'Role', disabled: true }
                  ]);
                }
              )
            ])
              .then(() => next())
              .catch(error => {
                store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' });
              });
          },
          meta: {
            toolbarTitle: 'Pracownik'
          }
        },
        {
          path: '/employees/:id/roles',
          name: 'employeePartnerRoles',
          component: userRoles,
          beforeEnter: (to, from, next) => {
            store.commit('employees/setId', to.params.id);

            Promise.all([
              store.dispatch(`profiles/userRole/list`, to.params.id),
              store.dispatch(`roles/list`, { count: 0 }),
              store.dispatch('employees/get').then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Pracownicy', to: { name: 'employees', exact: true } },
                  { text: res.firstName + ' ' + res.lastName, disabled: true }
                ]);
              })
            ])
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
          },
          meta: {
            toolbarTitle: 'Pracownik'
          }
        },
        {
          path: 'groups',
          name: 'groups',
          component: Groups,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Grupy', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Grupy mieszkańców'
          }
        },
        {
          path: 'clients',
          name: 'clientSearch',
          component: ClientProfileSearch,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Klienci', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Wyszukaj klienta'
          }
        },
        {
          path: 'clients/:id',
          component: ClientView,
          children: [
            {
              path: '',
              name: 'client',
              component: ClientProfile,
              beforeEnter: (to, from, next) => {
                store.commit('client/setId', to.params.id);

                store
                  .dispatch('client/get')
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Klienci', to: { name: 'clientSearch' }, exact: true },
                      { text: res.firstName + ' ' + res.lastName, disabled: true }
                    ]);

                    next();
                  })
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
              },
              meta: {
                toolbarTitle: 'Profil klienta'
              }
            },

            {
              path: 'usage',
              name: 'clientUsage',
              component: benefitsHistory,
              beforeEnter: (to, from, next) => {
                store.commit('client/setId', to.params.id);

                store
                  .dispatch('client/get')
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Klienci', to: { name: 'clientSearch' }, exact: true },
                      { text: res.firstName + ' ' + res.lastName, disabled: true }
                    ]);
                    store
                      .dispatch('benefitsHistory/list', {
                        start: 0,
                        count: DEFAULT_LIST_COUNT,
                        profileId: res.id,
                        desc: true
                      })
                      .then(() => next());
                  })
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
              },
              meta: {
                toolbarTitle: 'Profil klienta'
              }
            },
            {
              path: 'history',
              name: 'clientHistory',
              component: EntityHistory,
              beforeEnter: (to, from, next) => {
                store.commit('client/setId', to.params.id);

                Promise.all([
                  store.dispatch('client/get').then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Klienci', to: { name: 'clientSearch' }, exact: true },
                      { text: res.firstName + ' ' + res.lastName, disabled: true }
                    ]);
                  }),
                  store.dispatch('entityHistory/list', {
                    count: DEFAULT_LIST_COUNT,
                    entityId: to.params.id,
                    start: 0,
                    desc: true,
                    tableName: 'Profile'
                  })
                ])
                  .then(() => next())
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
              },
              meta: {
                toolbarTitle: 'Profil klienta'
              }
            },
            {
              path: 'groups',
              name: 'clientGroups',
              component: clientGroups,
              beforeEnter: (to, from, next) => {
                store.commit('client/setId', to.params.id);

                store
                  .dispatch('client/get')
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Klienci', to: { name: 'clientSearch' }, exact: true },
                      { text: res.firstName + ' ' + res.lastName, disabled: true }
                    ]);

                    next();
                  })
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
              },
              meta: {
                toolbarTitle: 'Profil klienta'
              }
            },
            {
              path: 'cards',
              name: 'clientCards',
              component: clientCards,
              beforeEnter: (to, from, next) => {
                store.commit('client/setId', to.params.id);

                store
                  .dispatch('client/get')
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Klienci', to: { name: 'clientSearch' }, exact: true },
                      { text: res.firstName + ' ' + res.lastName, disabled: true }
                    ]);

                    next();
                  })
                  .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
              },
              meta: {
                toolbarTitle: 'Profil klienta'
              }
            }
          ]
        },
        {
          path: 'officials/:id',
          name: 'official',
          component: OfficialProfile,
          beforeEnter: (to, from, next) => {
            store.commit('officials/setId', to.params.id);

            store
              .dispatch('officials/get')
              .then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Urzędnicy', to: { name: 'officials' }, exact: true },
                  { text: res.firstName + ' ' + res.lastName, disabled: true }
                ]);

                next();
              })
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych urzędnika' }));
          },
          meta: {
            toolbarTitle: 'Profil urzędnika'
          }
        },
        {
          path: 'officials',
          name: 'officials',
          component: OfficialsList,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Urzędnicy', disabled: true }]);
            next();
          }
        },
        {
          path: 'officials/roles/:id',
          name: 'officialRoles',
          component: userRoles,
          beforeEnter: (to, from, next) => {
            store.commit('officials/setId', to.params.id);
            Promise.all([
              store.dispatch(`profiles/userRole/list`, to.params.id),
              store.dispatch(`roles/list`, { count: 0 }),
              store.dispatch('officials/get').then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Urzędnicy', to: { name: 'officials' }, exact: true },
                  { text: res.firstName + ' ' + res.lastName, disabled: true },
                  { text: 'Role', disabled: true }
                ]);
              })
            ])
              .then(() => next())
              .catch(error => {
                store.dispatch('notifyError', { title: 'Nie udało się pobrać danych urzędnika' });
              });
          },
          meta: {
            toolbarTitle: 'Drzewo ról użytkownika'
          }
        },
        {
          path: 'settings',
          name: 'settings',
          component: Settings,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Ustawienia', disabled: true }]);

            store
              .dispatch('settingGroups/list')
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać grup ustawień' }));
          },
          meta: {
            toolbarTitle: 'Ustawienia'
          }
        },
        {
          path: 'settings/:id',
          name: 'settingsById',
          component: Settings,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Ustawienia', disabled: true }]);

            store
              .dispatch('settingGroups/list')
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać grup ustawień' }));
          },
          meta: {
            toolbarTitle: 'Ustawienia'
          }
        },
        {
          path: 'templates',
          name: 'templates',
          component: TemplatesList,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Szablony', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Szablony wiadomości'
          }
        },
        {
          path: 'roles/:id',
          name: 'rolePermission',
          component: RolePermission,
          beforeEnter: (to, from, next) => {
            store
              .dispatch('roles/get', parseInt(to.params.id))
              .then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Role', to: { name: 'roles' }, exact: true },
                  { text: res.name, disabled: true }
                ]);

                next();
              })
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych roli' }));
          },
          meta: {
            toolbarTitle: 'Drzewo ról'
          }
        },
        {
          path: 'roles',
          name: 'roles',
          component: RolesTree,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Role', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Drzewo ról'
          }
        },
        {
          path: 'categories',
          name: 'categories',
          component: Categories,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Kategorie', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Lista kategorii'
          }
        },
        {
          path: 'terms',
          name: 'terms',
          component: Terms,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Regulaminy', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Lista regulaminów'
          }
        },
        {
          path: 'employees/:employeeId',
          name: 'employeePartner',
          component: EmployeeProfile,
          beforeEnter: (to, from, next) => {
            store.commit('employees/setId', to.params.employeeId);

            store
              .dispatch('employees/get')
              .then(res => {
                store.commit('breadcrumbs/set', [
                  { text: 'Pracownicy', to: { name: 'employees' }, exact: true },
                  { text: res.firstName + ' ' + res.lastName, disabled: true }
                ]);
                next();
              })
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
          },
          meta: {
            toolbarTitle: 'Pracownik'
          }
        },
        {
          path: 'employees',
          name: 'employees',
          component: PartnerEmployees,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Pracownicy', disabled: true }]);
            next();
          },
          meta: {
            toolbarTitle: 'Pracownicy'
          }
        },
        {
          path: 'pages',
          name: 'pages',
          component: Pages,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Strony', disabled: true }]);
            next();
          }
        },
        {
          path: 'pages/:slot',
          name: 'pagesBySlot',
          component: Pages,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Strony', disabled: true }]);
            next();
          }
        }
      ]
    },
    { path: '*', component: PageNotFound, meta: { authorized: true } }
  ]
} as RouterOptions);

router.beforeEach((to: any, from: any, next: any) => {
  if (to.matched.some((record: any) => record.meta.authorized)) {
    if (sessionStorage.getItem(SessionStorage.TOKEN) === null) {
      next({
        path: '/login',
        params: { redirectURL: to.fullPath }
      });
    } else if (isPartner()) {
      const partnerId = parseInt(sessionStorage.getItem(SessionStorage.PARTNER_ID) as string);
      store.commit('partners/setId', partnerId);
      Promise.all([store.dispatch('partners/get'), store.dispatch('partners/places/list', { partnerId })]).then(() =>
        next()
      );
    } else {
      // Sprawdzanie roli
      next();
    }
  } else {
    next();
  }
});

export default router;
